import * as React from 'react';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { ApronBackSplashModel, ConfigurationModel, BuiltInPartsModel } from 'franke-trough-dependencies';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Tooltip, Button } from 'reactstrap';
import { requestAPIActions } from 'loopback-react-request';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './ApronBacksplash.messages';
import { hasSpaceForApron, requiredWidthForApron, requiredDistancesForApron, simulatedAprons, apronSpaceAvailable } from './Apron';

export interface ApronRequiredChoiceProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: any;
  id: string;
  requiredHeight?: number;
  plateBuilder: GlobalPrivateState['plateBuilder'];
}

export interface ApronRequiredChoiceState {
  tooltipOpen: boolean;
  ignorableTrigger: boolean;
  selectedDecision: 'skip' | 'short' | 'long' | 'nosleeve' | null;
}

export class ApronRequiredChoicePartial extends React.Component<ApronRequiredChoiceProps, ApronRequiredChoiceState> {
  constructor(props: ApronRequiredChoiceProps) {
    super(props);

    this.state = {
      tooltipOpen: false,
      ignorableTrigger: false,
      selectedDecision: null
    };
    this.toggle = this.toggle.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public render() {
    const tollTipId = `apron_required_tooltip_${this.props.id}`;

    return this.apronRequired ? (
      <>
        <span className="info-icon" id={tollTipId}>
          <FontAwesomeIcon icon={faInfo} />
          <Tooltip 
            autohide={false}
            isOpen={this.state.tooltipOpen} 
            placement="top"
            toggle={this.toggle}
            target={tollTipId}
            className="tooltip-choice"
          >
            <p>
              <FormattedMessage {...MESSAGES.apronHeightRequiredChoiceMessage}/>
            </p>
            <>
              <label>
                <input
                  type="radio"
                  name="decision"
                  value="short"
                  checked={this.state.selectedDecision === 'short'}
                  onClick={() => this.setState({ignorableTrigger: true})}
                  onChange={(ev: React.SyntheticEvent<HTMLInputElement>) => this.handleChange(ev)}
                />
                <FormattedMessage {...MESSAGES.apronHeightRequiredChoice60}/>
              </label>
              <label>
                <input
                  type="radio"
                  name="decision"
                  value="long"
                  checked={this.state.selectedDecision === 'long'}
                  onClick={() => this.setState({ignorableTrigger: true})}
                  onChange={(ev: React.SyntheticEvent<HTMLInputElement>) => this.handleChange(ev)}
                />
                <FormattedMessage {...MESSAGES.apronHeightRequiredChoice150}/>
              </label>
              <label>
                <input
                  type="radio"
                  name="decision"
                  value="skip"
                  checked={this.state.selectedDecision === 'skip'}
                  onClick={() => this.setState({ignorableTrigger: true})}
                  onChange={(ev: React.SyntheticEvent<HTMLInputElement>) => this.handleChange(ev)}
                />
                <FormattedMessage {...MESSAGES.apronHeightRequiredChoiceSkip}/>
              </label>
            </>
            <p className="text-right">
              <Button 
                onClick={(ev: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(ev, 'submit')}
                className="yes"
                disabled={this.state.selectedDecision === null}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
            </p>
          </Tooltip>
        </span>
      </>
    ) : null;
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>): void {
    const { value } = event.target as HTMLInputElement;

    this.setState({
      selectedDecision: value as any
    });
  }

  private get apron(): ApronBackSplashModel['apron'] | null | undefined {
    return this.configuration &&
      this.configuration.apronBackSplash &&
      this.configuration.apronBackSplash.apron;
  }

  private get apronHeight(): ApronBackSplashModel['apronHeight'] | null | undefined {
    return this.configuration &&
      this.configuration.apronBackSplash &&
      this.configuration.apronBackSplash.apronHeight;
  }

  private get apronRequired(): boolean {
    return this.apron === 'without' ||
      (this.apron === 'with' && !this.apronHeight) || 
      (this.apron === 'with' && (this.apronHeight && this.apronHeight < this.requiredHeight)) ? true : false;
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration && this.props.configuration.result;
  }

  private get requiredHeight(): number {
    return this.props.requiredHeight || 150;
  }

  private dispatchConfiguration() {
    if (!this.configuration || !this.props.plateBuilder) { return; }
    const { builtInParts, apronBackSplash, washBasinDimension } = this.configuration;

    // TODO ... Use default values for apronHeight
    if (!builtInParts || !apronBackSplash) { return; }

    if (this.state.selectedDecision === 'skip') {
      builtInParts.cutOutDisposalsApronRequired = false;
    }

    if (this.state.selectedDecision === 'nosleeve' && builtInParts.cutOutDisposalsType) {
      builtInParts.cutOutDisposalsType = builtInParts.cutOutDisposalsType.replace('_SLEEVE', '') as any;
    }

    if (this.state.selectedDecision === 'short') {
      apronBackSplash.apron = 'with';
      apronBackSplash.apronHeight = 60;
    }

    if (this.state.selectedDecision === 'long') {
      apronBackSplash.apron = 'with';
      apronBackSplash.apronHeight = 150;
    }

    const aprons = simulatedAprons(this.props.plateBuilder);
    const spaces = apronSpaceAvailable(this.props.plateBuilder);
    
    if ((this.state.selectedDecision === 'short' || this.state.selectedDecision === 'long') && !hasSpaceForApron(aprons, spaces)) {
      if (!washBasinDimension || !washBasinDimension.distances) { return; }

      apronBackSplash.restorablePlateDimensions = {
        plateWidth: washBasinDimension.plateWidth,
        distances: JSON.parse(JSON.stringify(washBasinDimension.distances))
      };
      washBasinDimension.plateWidth = requiredWidthForApron(aprons, spaces, washBasinDimension.plateWidth);
      washBasinDimension.distances = requiredDistancesForApron(aprons, spaces, washBasinDimension.distances)
    }

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {
            apronBackSplash,
            builtInParts,
            washBasinDimension
          }
        },
      },
      true,
    ));
  }

  private handleSubmit(event: React.MouseEvent<HTMLButtonElement>, type: 'submit' | 'cancel') {
    if (type === 'submit') {
      this.dispatchConfiguration();
    }
  }

  private toggle() {
    if (this.state.ignorableTrigger) {
      this.setState({
        ignorableTrigger: false
      });
    } else {
      this.setState({
        tooltipOpen: !this.state.tooltipOpen
      });
    }
  }
}

export const ApronRequiredChoice = connect((state: GlobalPrivateState) => ({
  plateBuilder: state.plateBuilder,
  configuration: state.configuration,
}))(ApronRequiredChoicePartial);

export default ApronRequiredChoice;


